import React from "react";
import PropTypes from "prop-types";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

const CALL_VOLUME_COLOR = "#90CAF9";
const CONNECTION_RATE_COLOR = "#4CAF50";
const ORDER_RATE_COLOR = "#9C27B0";

const formatHour = (hour) => {
  if (hour === 0) return "12 AM";
  if (hour === 12) return "12 PM";
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <div className="hourly-chart-tooltip">
      <p className="tooltip-hour">{formatHour(label)}</p>
      <p className="tooltip-calls">
        Total Calls: <strong>{data.totalCalls}</strong>
      </p>
      <p className="tooltip-connected">
        Connected: <strong>{data.connectedCalls}</strong> ({data.connectionRate.toFixed(1)}%)
      </p>
      <p className="tooltip-ordered">
        Ordered: <strong>{data.orderedCalls}</strong> ({data.orderRate.toFixed(1)}%)
      </p>
    </div>
  );
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.number
};

const HourlyDispositionChart = ({ hourlyDispositionData }) => {
  if (!hourlyDispositionData || hourlyDispositionData.length === 0) {
    return (
      <div className="graph-container hourly-disposition-chart">
        <h4 className="graph-header">Calls by Hour of Day</h4>
        <div className="no-data-message">No hourly data available</div>
      </div>
    );
  }

  const data = hourlyDispositionData.map(item => ({
    ...item,
    hourLabel: formatHour(item.hour)
  }));

  return (
    <div className="graph-container hourly-disposition-chart">
      <h4 className="graph-header">Calls by Hour of Day</h4>
      <p className="graph-subtitle">
        Identify optimal calling times based on connection and order rates
      </p>
      <ResponsiveContainer height={300}>
        <ComposedChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="hour"
            tickFormatter={formatHour}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            yAxisId="left"
            orientation="left"
            tick={{ fontSize: 11 }}
            label={{
              value: "Call Volume",
              angle: -90,
              position: "insideLeft",
              style: { textAnchor: "middle", fontSize: 11 }
            }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={[0, 100]}
            tick={{ fontSize: 11 }}
            tickFormatter={(value) => `${value}%`}
            label={{
              value: "Rate (%)",
              angle: 90,
              position: "insideRight",
              style: { textAnchor: "middle", fontSize: 11 }
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Bar
            yAxisId="left"
            dataKey="totalCalls"
            name="Total Calls"
            fill={CALL_VOLUME_COLOR}
            radius={[2, 2, 0, 0]}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="connectionRate"
            name="Connection Rate"
            stroke={CONNECTION_RATE_COLOR}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="orderRate"
            name="Order Rate"
            stroke={ORDER_RATE_COLOR}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

HourlyDispositionChart.propTypes = {
  hourlyDispositionData: PropTypes.arrayOf(
    PropTypes.shape({
      hour: PropTypes.number,
      totalCalls: PropTypes.number,
      connectedCalls: PropTypes.number,
      orderedCalls: PropTypes.number,
      connectionRate: PropTypes.number,
      orderRate: PropTypes.number
    })
  )
};

export default HourlyDispositionChart;
